import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

import { AppInsightsConfig } from './app-insights.types';

// Outside of service to ensure singleton instance across the app.
let reactPlugin: ReactPlugin | null = null;
let appInsights: ApplicationInsights | null = null;
let disabled = false;

// Fake plugin to for when the app insights is skipped from init.
const noopPlugin = {
  processTelemetry: () => undefined,
  trackMetric: () => undefined,
  trackPageView: () => undefined,
  trackEvent: () => undefined,
  trackException: () => undefined,
  trackTrace: () => undefined,
} as unknown as ReactPlugin;

/**
 * This is needed for a transition period where most app pipelines still use
 * pure instrumentation keys, but we want to support connection strings as well.
 */
function keyBasedConfig(key: string) {
  if (key.startsWith('InstrumentationKey=') || key.startsWith('ikey=')) {
    return { connectionString: key };
  }

  return { instrumentationKey: key };
}

/**
 * Factory function to create telemetry service.
 * This is used to ensure that the telemetry service is a
 * singleton across the app and can be initialized with
 * different configurations if needed.
 *
 * This can also be used within the angular
 */
export function createTelemetryService() {
  function initialize({
    instrumentationKey,
    cloudRole,
    appName,
    customConfig,
  }: AppInsightsConfig) {
    if (!instrumentationKey) {
      disabled = true;
      console.warn(
        '[AppInsights] Key or connection string not provided. Telemetry is disabled.',
      );
      return;
    }

    // This means that the service has already been initialized,
    // and we don't need to do it again.
    if (reactPlugin != null && appInsights != null) return;

    disabled = false;

    reactPlugin = new ReactPlugin();
    appInsights = new ApplicationInsights({
      config: {
        extensions: [reactPlugin],
        autoTrackPageVisitTime: true,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        ...customConfig,
        ...keyBasedConfig(instrumentationKey),
      },
    });

    appInsights.loadAppInsights();
    appInsights.addTelemetryInitializer((telemetry) => {
      telemetry.tags = telemetry.tags || [];
      telemetry.tags['ai.cloud.role'] = cloudRole;
      telemetry.data = telemetry.data || {};
      telemetry.data['ms-appName'] = appName;
    });
  }

  /**
   * Clean up function to reset the telemetry service.
   * This was added to facilitate testing, allowing us to reset the
   * state of the telemetry service between tests.
   */
  function cleanUp() {
    reactPlugin = null;
    appInsights = null;
    disabled = false;
  }

  return {
    initialize,
    cleanUp,
    get reactPlugin() {
      return reactPlugin;
    },
    get contextPlugin() {
      return reactPlugin ?? noopPlugin;
    },
    get appInsights() {
      return appInsights;
    },
    get disabled() {
      return disabled;
    },
  };
}

export const ai = createTelemetryService();
export type AppInsightsService = ReturnType<typeof createTelemetryService>;
